import { createTheme } from '@mui/material/styles';
import {
  COLORS,
  FONTS,
  HAIRLINE,
  MOTION,
  RADII,
  RAW_COLORS,
  SHADOW,
  softTint,
} from './tokens';

export type ResolvedThemeMode = 'light' | 'dark';

const paletteByMode = {
  light: {
    backgroundDefault: RAW_COLORS.surfaceApp,
    backgroundPaper: RAW_COLORS.surfaceCard,
    textPrimary: RAW_COLORS.ink,
    textSecondary: RAW_COLORS.inkSecondary,
    secondary: RAW_COLORS.navy,
  },
  dark: {
    backgroundDefault: '#0F1724',
    backgroundPaper: '#172234',
    textPrimary: '#F3F6FA',
    textSecondary: '#C0CAD8',
    secondary: '#C9D7EA',
  },
} as const;

/**
 * Global GritCargo theme (brand-aligned product system).
 *
 * Historically this theme was deliberately near-empty and brand styling lived
 * only inside the modal themes. The v1.1 rebrand promotes the brand defaults
 * here so the identity lands app-wide: Inter body + Open Sans headings, Signal
 * Green pill primaries, navy outlined buttons, and the brand surfaces. Dialogs
 * still wrap in `modalTheme` / `modalTheme2` for their dialog-specific shells —
 * the two stay in sync with this file.
 */
export const createGritTheme = (mode: ResolvedThemeMode = 'light') => {
  const palette = paletteByMode[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: RAW_COLORS.signalGreen,
        contrastText: RAW_COLORS.signalGreenInk,
      },
      secondary: {
        main: palette.secondary,
        contrastText: mode === 'dark' ? RAW_COLORS.ink : '#FFFFFF',
      },
      error: { main: RAW_COLORS.statusAlert },
      warning: { main: RAW_COLORS.statusCaution },
      info: { main: RAW_COLORS.statusInfo },
      success: { main: RAW_COLORS.statusGood },
      background: {
        default: palette.backgroundDefault,
        paper: palette.backgroundPaper,
      },
      text: { primary: palette.textPrimary, secondary: palette.textSecondary },
    },
    typography: {
      fontFamily: FONTS.body,
      // Headings use Open Sans with a fixed in-app scale (DESIGN.md No-Hero
      // Rule: the page-title tier caps at 1.5rem). Sizes descend with the
      // variant so hierarchy comes from the semantic level, not per-page sx.
      h1: {
        fontFamily: FONTS.heading,
        fontWeight: 700,
        fontSize: '1.5rem',
        lineHeight: 1.2,
        color: COLORS.navy,
      },
      h2: {
        fontFamily: FONTS.heading,
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: 1.3,
        color: COLORS.navy,
      },
      h3: {
        fontFamily: FONTS.heading,
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.35,
        color: COLORS.navy,
      },
      h4: {
        fontFamily: FONTS.heading,
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.4,
        color: COLORS.navy,
      },
      h5: {
        fontFamily: FONTS.heading,
        fontWeight: 600,
        fontSize: '0.9375rem',
        lineHeight: 1.4,
        color: COLORS.navy,
      },
      h6: {
        fontFamily: FONTS.heading,
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.4,
        color: COLORS.navy,
      },
      subtitle1: { fontFamily: FONTS.heading },
      subtitle2: { fontFamily: FONTS.heading },
      // Button labels: Inter SemiBold, sentence case (handled in component override).
      button: {
        fontFamily: FONTS.body,
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: { borderRadius: RADII.md },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: COLORS.surfaceApp,
            color: COLORS.onSurface,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            'textTransform': 'none',
            'fontFamily': FONTS.body,
            'fontWeight': 600,
            'borderRadius': RADII.pill,
            'padding': '6px 18px',
            'boxShadow': 'none',
            '&.Mui-disabled': {
              backgroundColor: COLORS.disabledBg,
              color: COLORS.borderStrong,
            },
          },
          contained: {
            'backgroundColor': COLORS.signalGreen,
            'color': COLORS.signalGreenInk,
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: COLORS.signalGreenHover,
              boxShadow: SHADOW.hoverLift,
            },
          },
          // Destructive primary: red pill (use `color="error"` on contained buttons).
          containedError: {
            'backgroundColor': COLORS.statusAlert,
            'color': '#FFFFFF',
            'boxShadow': 'none',
            '&:hover': { backgroundColor: '#C42B2B', boxShadow: 'none' },
          },
          outlined: {
            'borderColor': COLORS.borderSoft,
            'color': COLORS.navy,
            '&:hover': {
              borderColor: COLORS.navy,
              backgroundColor: 'rgba(37, 48, 69, 0.04)',
            },
          },
          text: { color: COLORS.navy },
        },
      },
      MuiCircularProgress: {
        styleOverrides: { root: { color: COLORS.signalGreen } },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: COLORS.navy, textDecorationColor: COLORS.navy },
        },
      },
      // ── Form controls (DESIGN.md §5 Inputs) ─────────────────────────────────
      // Mirrors the modalTheme input language so in-page controls match dialog
      // controls: white surface, 8px radius, #C7C7C7 stroke → navy on
      // hover/focus, no glow. Height is deliberately NOT fixed here — compact
      // search fields and dense filters own their own height.
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // MUI's float animation includes `max-width` (a layout property).
            // The label glides on color + transform only; max-width snaps,
            // which only ever matters for over-long labels mid-float.
            'transition': `color ${MOTION.durationShort}ms ${MOTION.easeOut}, transform ${MOTION.durationShort}ms ${MOTION.easeOut}`,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            '&.Mui-focused': { color: COLORS.navy },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          // MUI's default thumb transition animates `left` alongside
          // transform; the thumb only ever moves on transform, so drop the
          // layout property from the list.
          switchBase: {
            'transition': `transform ${MOTION.durationShort}ms ${MOTION.easeOut}`,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            '&.Mui-error': { color: COLORS.statusAlertInk },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            'borderRadius': `${RADII.sm}px`,
            'backgroundColor': COLORS.surfaceCard,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.borderStrong,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.navy,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.navy,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.statusAlert,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.borderStrong,
            },
            '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.borderStrong,
            },
            // MUI animates the notch legend's width when the label floats — a
            // layout-property transition. The label itself still glides on
            // transform; the notch gap simply appears, which reads crisper.
            '& .MuiOutlinedInput-notchedOutline legend': {
              transition: 'none',
            },
          },
          input: {
            '&::placeholder': { color: COLORS.borderStrong },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: COLORS.navy },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            'borderRadius': `${RADII.sm}px`,
            'margin': '0px 8px',
            '&:hover': { backgroundColor: COLORS.surfaceApp },
            '&.Mui-selected': {
              backgroundColor: softTint(COLORS.signalGreen, 0.18),
            },
            '&.Mui-selected:hover': {
              backgroundColor: softTint(COLORS.signalGreen, 0.26),
            },
          },
        },
      },
      // Floating layers: menus / autocomplete popups sit above the plane —
      // floating shadow, hairline, 8px radius (scoped here, NOT on MuiPaper,
      // which would leak onto cards and drawers).
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: `${RADII.sm}px`,
            border: `1px solid ${HAIRLINE.card}`,
            boxShadow: SHADOW.floatingLayer,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: `${RADII.sm}px`,
            border: `1px solid ${HAIRLINE.card}`,
            boxShadow: SHADOW.floatingLayer,
          },
        },
      },
      // Tooltips are part of the navy frame: navy ground, white Inter text.
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: COLORS.frameNavy,
            color: COLORS.onFrame,
            fontFamily: FONTS.body,
            fontSize: '12px',
            fontWeight: 500,
            borderRadius: `${RADII.sm}px`,
            padding: '6px 10px',
          },
          arrow: { color: COLORS.frameNavy },
        },
      },
    },
  });
};

const theme = createGritTheme('light');

export default theme;
